import { z } from 'zod'
import type { FormField } from '@/lib/types'

/**
 * A zod schema derived from the form's own published fields.
 *
 * Derived, never written twice — the same argument `ValidationRules` makes on
 * the API side. A field added in the panel is validated the moment it is
 * published, so there is no second list to keep in step and therefore no way
 * for the two to drift.
 *
 * **The API stays the authority.** What is checked here is only what the
 * payload tells us; the honeypot, the timing trap and an editor's own error
 * message live server-side and are invisible from here. A `422` is still
 * mapped onto the inputs, and always will be.
 *
 * **The messages are Laravel's, verbatim.** The same failure can be caught
 * here or one round trip later by the API, and a form that phrases one problem
 * two ways depending on where it was noticed is worse than one that always
 * waits for the server. Zod's own defaults are written for a developer —
 * "Too small: expected string to have >=1 characters" names a wire format at
 * somebody filling in a contact form.
 */

/**
 * Mirrored from `ValidationRules::MAX_SHORT` and `MAX_LONG`.
 *
 * These are the one thing here that is *not* derived: the payload does not
 * publish a maximum length, so the numbers are duplicated and can drift. They
 * are worth having anyway — without them a visitor writes a long answer and
 * learns it was too long only from the server — but if the server's bounds
 * ever move, this is where the disagreement will be.
 */
const MAX_SHORT = 1000

const MAX_LONG = 10_000

const LAYOUT_TYPES = new Set(['heading', 'paragraph', 'divider'])

/**
 * The visitor-facing name of a field, so a message reads "The Email address
 * field is required" rather than naming the wire key at somebody filling in a
 * form. `attributes()` does exactly this server-side.
 *
 * An empty label means "not set" as it does everywhere in this payload, so the
 * name is the fallback — never the empty string, which would read as
 * "The  field is required."
 */
function attribute(field: FormField): string {
    return field.label !== undefined && field.label !== '' ? field.label : (field.name ?? 'field')
}

/** Laravel's own wording, keyed the way its language file keys it. */
const MESSAGES = {
    required: (name: string) => `The ${name} field is required.`,
    accepted: (name: string) => `The ${name} field must be accepted.`,
    email: (name: string) => `The ${name} field must be a valid email address.`,
    date: (name: string) => `The ${name} field must be a valid date.`,
    numeric: (name: string) => `The ${name} field must be a number.`,
    in: (name: string) => `The selected ${name} is invalid.`,
    minNumeric: (name: string, min: number) => `The ${name} field must be at least ${min}.`,
    maxNumeric: (name: string, max: number) => `The ${name} field must not be greater than ${max}.`,
    maxString: (name: string, max: number) => `The ${name} field must not be greater than ${max} characters.`,
    minArray: (name: string, min: number) => `The ${name} field must have at least ${min} items.`,
} as const

/** The values an editor offered, dropping anything malformed. */
function optionValues(field: FormField): string[] {
    return (field.options ?? [])
        .map((option) => option.value)
        .filter((value): value is string => typeof value === 'string' && value !== '')
}

function isInput(field: FormField): field is FormField & { name: string } {
    return !LAYOUT_TYPES.has(field.type) && typeof field.name === 'string' && field.name !== ''
}

/**
 * An answer confined to the values the editor actually offered.
 *
 * A field with no options accepts nothing rather than everything: an empty or
 * malformed options list is a broken field, and letting an answer through
 * would store something the panel can never display.
 */
function choice(field: FormField): z.ZodType<string> {
    const values = optionValues(field)
    const message = MESSAGES.in(attribute(field))

    if (values.length === 0) {
        return z.string().refine(() => false, message) as unknown as z.ZodType<string>
    }

    return z.enum(values as [string, ...string[]], { error: message }) as unknown as z.ZodType<string>
}

/**
 * A field an editor did not mark required accepts being left alone, and
 * anything it *does* hold is still checked.
 *
 * The empty case is spelled out rather than made optional so that "left empty"
 * and "filled in wrongly" stay different answers — the second still gets the
 * rule's own message.
 */
function optionalOr(rule: z.ZodType): z.ZodType {
    return z.union([z.literal(''), rule])
}

/**
 * "Fill this in first, then it has to be valid."
 *
 * Two rules rather than one so the two failures keep their own words: an empty
 * box is required, and a filled one is judged by its own rule. The cast is
 * zod's: a coerced number declares its input as `unknown`, which a string
 * pipeline cannot express.
 */
function requiredThen(name: string, rule: z.ZodType): z.ZodType {
    return z
        .string({ error: MESSAGES.required(name) })
        .min(1, { error: MESSAGES.required(name) })
        .pipe(rule as z.ZodType<unknown, string>)
}

function ruleFor(field: FormField): z.ZodType {
    const name = attribute(field)
    const required = field.required === true

    switch (field.type) {
        // A consent box that can be skipped is not consent, so this ignores
        // the stored flag exactly as the API's `accepted` rule does.
        case 'consent':
            return z.literal(true, { error: MESSAGES.accepted(name) })

        case 'checkbox':
            return z.boolean()

        case 'email': {
            const address = z
                .email({ error: MESSAGES.email(name) })
                .max(MAX_SHORT, { error: MESSAGES.maxString(name, MAX_SHORT) })

            return required ? requiredThen(name, address) : optionalOr(address)
        }

        case 'number': {
            let number = z.coerce.number({ error: MESSAGES.numeric(name) })

            if (typeof field.min === 'number') {
                number = number.min(field.min, { error: MESSAGES.minNumeric(name, field.min) })
            }

            if (typeof field.max === 'number') {
                number = number.max(field.max, { error: MESSAGES.maxNumeric(name, field.max) })
            }

            // An empty box coerces to 0, which would quietly pass a required
            // number as zero — so emptiness is refused before coercion.
            return required ? requiredThen(name, number) : optionalOr(number)
        }

        case 'date': {
            // The wire format the endpoint documents. Anything else is a date
            // the API would refuse, so refusing it here is the same answer
            // sooner — in the same words.
            const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: MESSAGES.date(name) })

            return required ? requiredThen(name, date) : optionalOr(date)
        }

        case 'select':
        case 'radio': {
            const option = choice(field)

            return required ? requiredThen(name, option) : optionalOr(option)
        }

        case 'checkboxes': {
            const list = z.array(choice(field))

            return required ? list.min(1, { error: MESSAGES.minArray(name, 1) }) : list
        }

        case 'textarea':
        case 'text':
        default: {
            // The default also covers a type this frontend has never heard of:
            // a form deployed before a field type shipped still has to render
            // and submit, so an unknown input is a string and the API decides.
            const max = field.type === 'textarea' ? MAX_LONG : MAX_SHORT
            const string = z.string({ error: MESSAGES.required(name) }).max(max, {
                error: MESSAGES.maxString(name, max),
            })

            return required ? string.min(1, { error: MESSAGES.required(name) }) : string
        }
    }
}

export function buildFormSchema(fields: FormField[]): z.ZodObject<Record<string, z.ZodType>> {
    const shape: Record<string, z.ZodType> = {}

    for (const field of fields) {
        if (isInput(field)) {
            shape[field.name] = ruleFor(field)
        }
    }

    return z.object(shape)
}

/** What each input starts at, so the form is controlled from the first render. */
export function defaultValuesFor(fields: FormField[]): Record<string, unknown> {
    const values: Record<string, unknown> = {}

    for (const field of fields) {
        if (!isInput(field)) {
            continue
        }

        if (field.type === 'checkbox' || field.type === 'consent') {
            values[field.name] = field.default === true
        } else if (field.type === 'checkboxes') {
            values[field.name] = []
        } else {
            values[field.name] = field.default ?? ''
        }
    }

    return values
}
